import { clientApi, globalApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { createUrlWithParams } from "@/utils/url";
import { TGetCompanyListReqDto, TGetCompanyListResDto, TGetCompanyOutputDto } from "@/api/services/main/company/dtos/get-company-list.dto";
import { TCreateCompanyReqDto, TCreateCompanyResDto } from "@/api/services/main/company/dtos/create-company-req.dto";
import { useParams } from "react-router-dom";
import { TGetCompanyResDto } from "@/api/services/main/company/dtos/get-company.dto";
import { TCompanyRowExcel } from "@/features/company/types";
import { TOption } from "@/types/common";
import { useMemo } from "react";
import { TGetSingedUrlResDto } from "@/api/services/global/file/dtos/get-signed-url";
import {
  TGetCompanyListByRsoResDto,
} from "@/api/services/main/company/dtos/get-company-list-by-rso.dto";
import {
  TCancelRsoStudyLinkResDto,
  TCreateRsoStudyLinkReqDto,
  TCreateRsoStudyLinkResDto,
  TGetRsoStudyLinkRequest,
  TGetRsoStudyLinkResDto,
  TUnlinkRsoStudyLinkResDto,
} from "@/api/services/main/rso/dtos/study-link-request.dto";
import { TSyncVisaResDto } from "@/api/services/main/rso/dtos/link-visa.dto";

export const fetchCompanyListApi = async (req: TGetCompanyListReqDto): Promise<TGetCompanyListResDto> => {
  const url = createUrlWithParams("/companies", req);

  const rs = await clientApi.get<TGetCompanyListResDto>(url);
  return rs.data;
};

export const useCompanyList = (req?: TGetCompanyListReqDto) => {
  const pagReq = usePagination();
  const params = req ?? pagReq;

  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST, params],
    queryFn: () => fetchCompanyListApi(params),
  });

  const companyOptions: TOption[] = useMemo(
    () => (data ? data?.data.filter((item) => !item.isArchive).map((item) => ({ label: item.companyName ?? "", value: item.id })) : []),
    [data],
  );

  return { data, companyOptions, ...rest };
};

export const updateCompanyApi = async (id: string | number, payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const rs = await clientApi.put<TCreateCompanyResDto>(`/company/${id}`, payload);
  return rs.data;
};

export const createCompanyApi = async (payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const rs = await clientApi.post<TCreateCompanyResDto>("/company", payload);
  return rs.data;
};

export const fetchCompanyInfoApi = async (id: string | number): Promise<TGetCompanyResDto> => {
  const { data } = await clientApi.get<TGetCompanyResDto>(`/company/${id}`);
  return data;
};

export const useCompanyInfo = (id?: number) => {
  const { companyId } = useParams();

  const fetchId = id ?? companyId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchCompanyInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const validateImportCompanyApi = async (payload: TCompanyRowExcel[]) => {
  const rs = await clientApi.post("/company/validate-import", payload);
  return rs.data;
};

export const importCompanyApi = async (payload: TGetCompanyOutputDto[]) => {
  const rs = await clientApi.post("/company/import", payload);
  return rs.data;
};

export const setCompanyArchive = async (companyId: number, isArchive: boolean = false) => {
  const rs = await clientApi.patch(`/company/${companyId}/archive`, {
    isArchive,
  });
  return rs.data;
};

export const getCompanyExists = async (companyName: string, companyId?: string) => {
  const rs = await clientApi.get("/company/exist-company-name", {
    params: {
      companyName: companyName,
      companyId,
    },
  });
  return rs.data;
};

export const fetchSignedUrlApi = async (filePath: string | null | undefined): Promise<TGetSingedUrlResDto> => {
  const url = createUrlWithParams("/files/signed-url", { filePath });
  const rs = await globalApi.get(url);
  return rs.data;
};

export const useCompanyFileSignedUrl = (filePath?: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_COMPANY_SIGNED_URL, filePath],
    queryFn: () => (filePath ? fetchSignedUrlApi(filePath) : undefined),
    enabled: Boolean(filePath),
  });
};

export const getCompanyListByRsoApi = async (): Promise<TGetCompanyListByRsoResDto> => {
  const { data } = await clientApi.get<TGetCompanyListByRsoResDto>("/companies");
  return data;
};

export const useGetListCompanyByRso = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_COMPANY_LIST],
    queryFn: () => getCompanyListByRsoApi(),
    enabled,
  });
};

export const deleteCompanyApi = async (companyId: number) => {
  const rs = await clientApi.delete(`/company/${companyId}`);
  return rs.data;
};

// ───────────── irohana visa 連携 (独立企業 = COMPANY_USER 向け) ─────────────
// RSO の study-link 群と同形。BE 側でログイン中企業 (identity.companyId) を申請元として解決する。

/** ログイン中企業を申請元として Visa へ連携申請する。 */
export const createCompanyStudyLinkRequestApi = async (payload: TCreateRsoStudyLinkReqDto): Promise<TCreateRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TCreateRsoStudyLinkResDto>("/company/study-link-requests", payload);
  return data;
};

/**
 * ログイン中企業の現在の連携申請を irohana visa と状態同期する。
 * BE 側で Visa へ問い合わせて最新ステータスを保存し、承認済みなら有効連携を作成する。申請がなければ null。
 */
export const getCurrentCompanyStudyLinkApi = async (): Promise<TGetRsoStudyLinkRequest | null> => {
  const { data } = await clientApi.get<TGetRsoStudyLinkResDto>("/company/study-link-requests");
  return data.data ?? null;
};

// 認証待ち中のポーリング間隔 (Visa 側で承認されたら自動で UI を更新するため)
const COMPANY_STUDY_LINK_SYNC_INTERVAL_MS = 8000;

/**
 * 認証待ち (pending) の間、Visa と状態を同期し続けるためのクエリ。
 * enabled が true の場合のみ実行し、一定間隔とウィンドウフォーカス時に再取得する。
 */
export const useSyncCompanyStudyLink = (enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.SYNC_STUDY_LINK],
    queryFn: getCurrentCompanyStudyLinkApi,
    enabled,
    refetchInterval: enabled ? COMPANY_STUDY_LINK_SYNC_INTERVAL_MS : false,
    refetchOnWindowFocus: enabled,
    gcTime: 0,
  });
};

/**
 * ログイン中企業の irohana visa 連携を解除する。
 * BE 側で Visa へ解除を依頼し、ローカルの有効連携 (rso_links) を解除確定する。
 */
export const unlinkCompanyStudyLinkApi = async (): Promise<TUnlinkRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TUnlinkRsoStudyLinkResDto>("/company/study-link-requests/unlink");
  return data;
};

/**
 * ログイン中企業の認証待ち (pending) の連携申請を取り消す。
 * BE 側で Visa へ取消を依頼し、ローカルの申請ステータスを cancelled に確定する。
 */
export const cancelCompanyStudyLinkRequestApi = async (): Promise<TCancelRsoStudyLinkResDto> => {
  const { data } = await clientApi.post<TCancelRsoStudyLinkResDto>("/company/study-link-requests/cancel");
  return data;
};

/**
 * ログイン中企業の有効連携の irohana visa 情報 (外国人数) を再同期する。
 * BE が Visa へ問い合わせて talentCount を取得・キャッシュ更新する。完了後 FE は企業詳細を再取得する。
 */
export const syncCompanyVisaInformationApi = async (): Promise<TSyncVisaResDto> => {
  const { data } = await clientApi.post<TSyncVisaResDto>("/company/study-link-requests/sync");
  return data;
};

